<template>
    <div>
        <headline>
            Benachrichtigungen
            <template slot="more">
                <button-primary @click="submit">
                    <template slot="icon"><i class="fa fa-paper-plane"></i></template> Benachrichtigung senden
                </button-primary>
            </template>
        </headline>

        <action class="my-8">
            <input-text v-model="message" :headline="false">
                Vergiss nicht deinen Urlaubskalender aktuell zu halten. 
                <template slot="helper">
                    Nachrichten werden an die <span class="font-bold">App</span> und die <span class="font-bold">E-Mail</span> versendet.
                </template>
            </input-text>
            <template slot="header">Nachricht</template>
            <div class="mb-2">
                <a @click="user.selected = false"
                   v-for="user in recipients"
                   class="cursor-pointer inline-flex items-center m-1 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-indigo-100 text-indigo-800">
                    {{ user.first_name }} {{ user.name }} {{ user.personnel_number }}
                    <button type="button"
                            class="flex-shrink-0 ml-1.5 inline-flex text-indigo-500 focus:outline-none focus:text-indigo-700"
                            aria-label="Remove small badge">
                        <svg class="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                            <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7"/>
                        </svg>
                    </button>
                </a>

            </div>

            <template slot="action">
                <div class="flex justify-end items-center space-x-2">
                    {{ recipients.length }} Empfänger

                    <button-secondary @click="state = 'select'" class="ml-4">
                        <template slot="icon"><i class="fa fa-plus"></i></template> Empfänger auswählen
                    </button-secondary>
                </div>
            </template>

        </action>

        <portal to="modal" v-if="state !== 'input'">
            <modal @close="state='input'">
                <template slot="header">
                    <span v-show="state !== 'select'">Benachrichtigung</span>
                    <span v-show="state === 'select'">Empfänger auswählen</span>
                </template>

                <div v-if="state==='loading'">
                    <loading></loading>
                </div>

                <div v-if="state==='ok'">Nachricht wurde versendet.</div>

                <div v-if="state==='failed'">Irgendwas ist schief gegangen.</div>

                <div v-if="state==='error'">{{ error }}</div>

                <div v-if="state === 'select'">

                    <div class="flex mb-4" v-show="isAlgorithm">
                        <button-secondary class="mr-4" @click="selectAllOwner()">
                            <i class="fa fa-plus mr-2"></i> Besitzer
                        </button-secondary>

                        <button-secondary class="mr-4" @click="selectAllTenants()">
                            <i class="fa fa-plus mr-2"></i> Nachfrager
                        </button-secondary>
                    </div>

                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
                            <table class="min-w-full">
                                <thead>
                                <tr>
                                    <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                                        <label>
                                            <input class="mr-2" type="checkbox" v-model="selectedAll"/> Alle
                                        </label>
                                    </th>
                                    <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                                        Benutzergruppen
                                    </th>
                                </tr>
                                </thead>
                                <tbody class="bg-white">
                                <tr v-for="(user, i) in items"
                                    :class="{'bg-gray-50': i % 2 !== 0 }">
                                    <td class="p-2">
                                        <input :id="`user_${user.id}`" type="checkbox" name="user_id[]"
                                               :value="user.id"
                                               v-model="user.selected"
                                               class="mr-2"
                                        >
                                        <span class="text-gray text-sm">
                                            <i class="fa fa-fw"
                                               :class="{'fa-desktop': user.desk_required === 'none', 'fa-search': user.desk_required !== 'none'}"></i>
                                        </span>
                                    </td>
                                    <td class="p-2"><label class="block mt-1" :for="`user_${user.id}`">
                                        {{ user.first_name }} {{ user.name }}</label>
                                    </td>
                                    <td class="p-2"><label class="block mt-1" :for="`user_${user.id}`">
                                        {{ user.email }}</label>
                                    </td>
                                    <td class="p-2"><label class="block mt-1" :for="`user_${user.id}`">
                                        {{ user.user_groups.map(item => item.name).join(', ') }}</label>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="flex justify-end mt-4">
                            <button-primary @click="state='input'">
                                <template slot="icon"><i class="fa fa-check"></i></template> Bestätigen
                            </button-primary>
                        </div>

                    </div>
                </div>
            </modal>
        </portal>
    </div>
</template>

<script>
import ButtonPrimary from "../components/UI/ButtonPrimary";
import Modal from "../components/Modal";
import Loading from "../components/UI/Loading";
import Action from "../components/UI/Action";
import ButtonSecondary from "../components/UI/ButtonSecondary";
import Headline from "../components/UI/Headline";
import InputText from "../components/UI/InputText";

export default {
    name: "Notification",
    components: {ButtonSecondary, Action, Loading, Modal, ButtonPrimary, Headline, InputText},
    props: ['users'],

    inject: ['group'],

    data: () => ({
        message: '',
        checked: [],
        state: 'input',
        items: [],
        error: '',
    }),

    created() {
        this.items = this.users.map(function (user) {
            user.selected = false;
            return user;
        });
    },

    computed: {
        isValid() {
            return this.message.length > 0 && this.recipients.length > 0;
        },
        recipients() {
            return this.items.filter(function (user) {
                return user.selected;
            });
        },
        isAlgorithm() {
            return this.group.allocation_mode === 'algorithm';
        },
        selectedAll: {
            get: function () {
                return this.items.every((user) => user.selected);
            },
            set: function (value) {
                this.users.forEach(function (user) {
                    user.selected = value;
                });
            }
        },
        tenants() {
            return this.items.filter((user) => user.desk_required !== 'none');
        },
        selectedTenants: {
            get: function () {
                return this.checked.length === this.users.length;
            },
            set: function (value) {
                let selected = this.checked;

                if (value) {
                    this.users.filter(function (user) {
                        return user.desk_required !== 'none';
                    }).forEach(function (user) {
                        selected.push(user.id);
                    });
                }

                this.checked = selected;
            }
        }
    },

    methods: {
        submit() {
            if (this.message.length === 0) {
                this.state = 'error';
                this.error = "Bitte das Nachricht Feld ausfüllen.";
                return;
            }
            if (this.recipients.length === 0) {
                this.state = 'error';
                this.error = "Keine Empfänger ausgewählt.";
                return;
            }
            this.state = 'loading';
            window.axios.post(`/admin/notifications`, {
                message: this.message,
                user_id: this.recipients.map(function (user) {
                    return user.id;
                }),
            })
                .then(() => {
                    this.state = 'ok';
                })
                .catch(() => {
                    this.state = 'failed';
                });
        },
        toggle() {

        },
        toggleTenants() {

        },
        selectAllOwner() {
            this.users.filter((user) => {
                return user.desk_required === 'none';
            }).filter((user) => {
                return !this.checked.includes(user.id);
            }).forEach((user) => {
                user.selected = true;
            });
        },
        selectAllTenants() {
            this.users.filter((user) => {
                return user.desk_required !== 'none';
            }).filter((user) => {
                return !this.checked.includes(user.id);
            }).forEach((user) => {
                user.selected = true;
            });
        },
    }
}
</script>

<style scoped>

</style>
